<script setup>
/**
 * The shape of the book, in four numbers.
 *
 * Three of them are doorways: each opens the Policyholders list with the filters
 * that produced it, so the list holds exactly the people the number counted.
 * The server computes them from the same queries that list is built from — see
 * PolicyholderCounts — and this component's only job is to carry the current
 * broker filter along so the destination is scoped the way the Overview was.
 *
 * The total is not a doorway. Its two halves live in different tables with no
 * reliable join between them, so there is no single list that contains both; it
 * reads as context, and XlKpi renders it as plain text with nothing to tab to.
 */
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { plural } from '../../../helpers/plural';
import { useBrokerScopeStore } from '../../../stores/brokerScope';
import XlKpiRow from '../../xl/XlKpiRow.vue';
import XlSkeleton from '../../xl/XlSkeleton.vue';

const props = defineProps({
  counts: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  // "Your book today" for a broker, who sees only the people assigned to them.
  heading: { type: String, default: 'The hub today' },
});

const router = useRouter();
const brokerScope = useBrokerScopeStore();

const days = computed(() => props.counts?.renewing_days ?? 30);

const cards = computed(() => {
  const c = props.counts;
  if (!c) return [];

  const row = [
    {
      key: 'policyholders',
      label: 'Policyholders',
      value: c.policyholders ?? 0,
      clickable: false,
      hint: '',
      tooltip: 'Everyone on the books: those who have signed in, plus those invited who have not.',
    },
    {
      key: 'signed_in',
      label: 'Signed in',
      value: c.signed_in ?? 0,
      hint: `of ${c.policyholders ?? 0}`,
      tone: 'success',
      tooltip: 'Has signed in to the hub at least once.',
    },
    {
      key: 'never_signed_in',
      label: 'Never signed in',
      value: c.never_signed_in ?? 0,
      hint: '',
      tone: 'warning',
      tooltip: 'Invited to the hub and has not signed in yet.',
    },
  ];

  // Withheld rather than shown as nought when SSO could not be asked: "0
  // renewing" must never be able to mean "we could not find out".
  if (c.renewing_soon !== null && c.renewing_soon !== undefined) {
    row.push({
      key: 'renewing_soon',
      label: `Renewing ≤${plural(days.value, 'day')}`,
      value: c.renewing_soon,
      hint: 'needs attention',
      tone: 'error',
      tooltip: `Their soonest policy renews within ${plural(days.value, 'day')}. Renewals already past are counted too, and are listed first.`,
      testid: 'kpi-renewing-soon',
    });
  }

  return row;
});

/** Spread after the broker scope: a destination must never set broker_id, or it would replace the scope rather than narrow it. */
const DESTINATIONS = {
  signed_in: () => ({ tab: 'registered' }),
  never_signed_in: () => ({ tab: 'pending' }),
  renewing_soon: (d) => ({ tab: 'registered', renewal_within: String(d) }),
};

const open = (key) => {
  const destination = DESTINATIONS[key];
  if (!destination) return;

  router.push({
    path: '/policyholders',
    query: { ...brokerScope.query, ...destination(days.value) },
  });
};
</script>

<template>
  <div v-if="props.loading || cards.length" class="overview-counts">
    <h2 class="overview-counts__title xl-section-heading" data-testid="overview-counts-heading">{{ props.heading }}</h2>

    <XlSkeleton
      v-if="props.loading"
      height="96px"
      class="overview-counts-loading"
      data-testid="counts-loading"
    />
    <XlKpiRow
      v-else-if="cards.length"
      :cards="cards"
      data-testid="overview-counts"
      @select="open"
    />
  </div>
</template>

<style scoped>
.overview-counts__title {
  margin: 0 0 10px;
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.overview-counts__title::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--xl-color-border);
}

.overview-counts :deep(.xl-kpi:nth-child(n + 3) .xl-kpi__tip) {
  left: auto;
  right: 12px;
}

/* Matches the row it stands in for: the cards below carry the same bottom
   margin, so the page does not jump when the numbers arrive. */
.overview-counts-loading {
  margin-bottom: 20px;
}
</style>
